import { Organization } from "./Organization";
import { Price } from "./Price";
import { Style } from "./Style";
import { DesignStatus } from "./enums/DesignStatus";
import {
  ApprovalType,
  BaseLogo,
  GetOverallApprovalStatus,
  ImageRef,
  ProductRef,
} from "./BaseLogo";
import { StyleView } from "./StyleView";
import { DesignMeasurementLine } from "./DesignMeasurementLine";
import { EditAction } from "../../../data/models/responses/DesignDto";
import { ApprovalGroup, ApprovalStatus } from "./Approval";

// Re-export GetOverallApprovalStatus for use in other components
export { GetOverallApprovalStatus };

export function getDesignState(design: Design): DesignState {
  if (design.status === DesignStatus.Draft) {
    return DesignState.Draft;
  }

  if (design.status === DesignStatus.Approval) {
    return DesignState.Approval;
  }

  if (design.status === DesignStatus.Done) {
    return DesignState.Done;
  }

  if (design.status === DesignStatus.Rejected) {
    return DesignState.Rejected;
  }

  switch (GetOverallApprovalStatus(design.approval!)) {
    case ApprovalStatus.Rejected:
      return DesignState.Rejected;
    case ApprovalStatus.Approved:
      return DesignState.Approval;
    case ApprovalStatus.Pending:
      return DesignState.Pending;
  }
}

export enum DesignState {
  Draft = "Draft",
  Rejected = "Rejected",
  Pending = "Pending",
  Approval = "Approval",
  Done = "Done",
}

// Should the clone button show? Prefer the API's verdict (canClone), which is true only
// when the clone will actually resolve a garment — the NAV import nulls StyleId on the
// bulk of approved designs and 68% of those point at a style that has left the catalog,
// so cloning them 404s. canClone is undefined on the list stub, the order cart, an older
// API, or when the resolve flag is off; in those cases fall back to the previous gate (a
// style FK or a NAV style name) so nothing that used to be cloneable disappears.
export function canCloneDesign(design: Design): boolean {
  if (typeof design.canClone === "boolean") {
    return design.canClone;
  }
  return !!design.style || !!design.navStyleName;
}

export interface Design {
  id: number;
  updateAtUtc: Date;
  createdAtUtc: Date;
  createdBy: string;
  status: DesignStatus;
  designLogos: DesignLogo[];
  measurementLines?: DesignMeasurementLine[];
  approval: ApprovalGroup | null;
  displayImage?: ImageRef;
  customerId: number;
  customer: Organization;
  vendorId?: number;
  vendor?: Organization;
  styleId?: number;
  style?: Style;
  title: string;
  product: MultiProductRef | null;
  isEditable?: boolean;
  navManufacturerName?: string;
  navStyleName?: string;
  productMul?: string;
  // Whether the API resolved a garment for cloning. Only the single-design detail read
  // (GetDesign) sets it; it is undefined on the list stub, the order cart, an older API,
  // or when the resolve flag is off. Gate the clone button with canCloneDesign(), not
  // this raw value, so those cases fall back to the previous style/navStyleName gate.
  canClone?: boolean;
  heating: HeatingProductRef | null;
  // viewImages are only included in public routes and contain images with overlayed logos.
  viewImages?: StyleView[];
  approvalType?: ApprovalType;
  mainOrganizationId?: number;
  priceSort?: number;
  note?: string;
  // Customer-editable design note (flag designNoteEnabled) — distinct from `note`,
  // which is the jsUpload drawing note rendered into the design image.
  customerNote?: string;
  customerNoteUpdatedAtUtc?: Date;
  customerNoteUpdatedByName?: string;
  // True while a placed logo's product/prices are still owed by the API's
  // product-refresh worker (flag REACT_APP_PRODUCT_PENDING_ENABLED; read
  // only through utils/functions/productPending). Flows from DesignDto
  // through the mapper spread.
  pricePending?: boolean;
  // Number of logos the API moved to the first view during the last Change
  // Style because their view does not exist on the new style. Only the PUT
  // .../style response carries it; read through
  // utils/functions/changeStyleRelocatedLogos (flag changeStyleRelocatedToastEnabled).
  relocatedLogoCount?: number;
}

export interface MultiProductRef {
  mul: string;
  products: ProductRef[];
  totalPrices: Price[];
}

export interface HeatingProductRef {
  name: string;
  sku: string;
  prices: Price[] | undefined;
}

export function statusToString(status: DesignStatus): string {
  switch (status) {
    case DesignStatus.Unknown:
      return "Unknown";
    case DesignStatus.Draft:
      return "Draft";
    case DesignStatus.Rejected:
      return "Rejected";
    case DesignStatus.Approval:
      return "Validating";
    case DesignStatus.Done:
      return "Done";
    default:
      console.warn("unknown status", status);
      return "Unknown";
  }
}

export interface DesignLogo {
  id: number;
  inDesignId: string;
  logoX: number;
  logoY: number;
  viewId: StyleView["id"];
  logo: BaseLogo;
  logoId: number;
  action?: EditAction | null;
  createdAtUtc?: Date;
  updateAtUtc?: Date;
  createdBy?: string;
  angleRads: number;
  heightMm: number;
  widthMm: number;
  designerWidthMm: number;
  viewName: string;
  // The placed row's own style view (detail fetch only) - lets the placement
  // popup draw the highlight for imported designs without a style of their own.
  view?: StyleView | null;
  alternativeText?: string;
  alternativeTextImageUri?: string;
}
